import React, { useMemo } from "react";

interface CustomCodeRunnerProps {
  code: string;
  scope: Record<string, unknown>;
  label?: string;
}

/**
 * The single place a custom view's self-contained code is executed. The code is
 * compiled with `new Function`, receiving `React` followed by the provided scope values in order,
 * and rendered. Errors thrown by the code are caught and surfaced inline so a bad snippet shows a
 * message instead of crashing the iframe.
 *
 * The execution result is memoized on the code and the scope values: every execution re-evaluates
 * the bundle's module body, redefining each component in it, so executing on every render would
 * remount the entire custom tree — losing all of its state and re-running every fetch it makes.
 * RTK Query's structural sharing keeps unchanged scope values reference-stable across refetches
 * that return identical data, so such a refetch re-renders this component without touching the
 * rendered view. (A bundle that calls React hooks during module evaluation would break under
 * memoization — such bundles were already broken by the per-render re-execution this replaces.)
 */
export default function CustomCodeRunner({ code, scope, label = "Error" }: CustomCodeRunnerProps) {
  const rendered = useMemo(() => {
    try {
      const factory = new Function("React", ...Object.keys(scope), code);
      return { element: factory(React, ...Object.values(scope)) as React.ReactNode, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { element: null, error: message };
    }
    // The scope's values are the dependencies; its key set is fixed per call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, ...Object.values(scope)]);

  if (rendered.error !== null) {
    return <div className="text-danger p-3">{label}: {rendered.error}</div>;
  }

  return <>{rendered.element}</>;
}
